import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo.svg';
import { useAuthStore } from '../stores/authStore';
import { useIsMobile } from '../hooks/useMediaQuery';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import ForgotPasswordModal from './ForgotPasswordModal';
import SetPasswordModal from './SetPasswordModal';

export default function AuthPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showSetPassword, setShowSetPassword] = useState(false);

  const { login } = useAuthStore();
  const isMobile = useIsMobile();
  const { isSlowConnection, saveData } = useNetworkStatus();

  // Disable video on mobile or slow connections to save bandwidth
  const shouldShowVideo = !isMobile && !isSlowConnection && !saveData;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Authentication failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Sign up click - redirect to pricing page
  const handleSignUpClick = () => {
    navigate('/pricing');
  };

  return (
    <div className="w-screen h-screen flex items-center justify-center bg-dark-bg relative overflow-hidden">
      {/* Video background - only on desktop with good connection */}
      {shouldShowVideo && (
        <div className="absolute inset-0 overflow-hidden">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          >
            <source
              src="https://video.gumlet.io/6752843cff6640815e8dd3a2/69111a3bf72550e2c5e350e0/download.mp4"
              type="video/mp4"
            />
          </video>
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-dark-bg/80"></div>
        </div>
      )}

      <div className="relative w-full max-w-md p-6">
        {/* Logo & Title */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center mb-6">
            <div className="relative">
              <img src={logo} alt="PARACLETE" className="h-20 w-auto drop-shadow-lg" />
              <div className="absolute inset-0 bg-accent-blue blur-xl opacity-50"></div>
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-3">
            <span className="bg-gradient-to-r from-accent-blue via-purple-400 to-accent-orange bg-clip-text text-transparent">
              Welcome to PARACLETE
            </span>
          </h1>
          <p className="text-dark-text-muted text-sm">
            AI-Powered Clipboard Bridge for Christian Research
          </p>
        </div>

        {/* Auth Card */}
        <div className="bg-dark-card backdrop-blur-xl border border-dark-border rounded-2xl shadow-2xl overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-dark-border">
            <button
              className="flex-1 py-4 text-sm font-semibold transition-all relative text-dark-text"
            >
              Sign in
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-accent-blue to-accent-orange"></div>
            </button>
            <button
              onClick={handleSignUpClick}
              className="flex-1 py-4 text-sm font-semibold transition-all relative text-dark-text-muted hover:text-dark-text"
            >
              Sign up
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-5">
            <div>
              <label className="block text-sm font-medium text-dark-text-muted mb-2">
                Email address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ fontSize: '16px' }}
                className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-3 text-dark-text placeholder-dark-text-muted/50 focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue transition-all"
                placeholder="you@example.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-text-muted mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ fontSize: '16px' }}
                className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-3 text-dark-text placeholder-dark-text-muted/50 focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue transition-all"
                placeholder="Enter your password"
                required
              />
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-6 h-6 rounded border-dark-border bg-dark-bg checked:bg-accent-blue focus:ring-2 focus:ring-accent-blue/50"
                />
                <span className="text-dark-text-muted">Remember me</span>
              </label>
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-accent-blue hover:underline"
              >
                Forgot password?
              </button>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-gradient-to-r from-accent-blue to-accent-orange text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-accent-blue/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Signing in...
                </span>
              ) : (
                <span>Sign in</span>
              )}
            </button>
          </form>
        </div>

        {/* VA Members Link */}
        <div className="text-center mt-6">
          <button
            onClick={() => setShowSetPassword(true)}
            className="text-sm text-accent-blue hover:underline transition-colors"
          >
            Existing VA Members
          </button>
        </div>

        {/* Footer */}
        <p className="text-center text-dark-text-muted text-xs mt-8">
          By continuing, you agree to Ministry Builders'{' '}
          <a href="https://ministry.builders/terms" className="text-accent-blue hover:underline" target="_blank" rel="noopener noreferrer">Terms of Service</a>
          {' '}and{' '}
          <a href="https://ministry.builders/privacy" className="text-accent-blue hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        </p>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />

      {/* Set Password Modal */}
      <SetPasswordModal
        isOpen={showSetPassword}
        onClose={() => setShowSetPassword(false)}
      />
    </div>
  );
}
